import { useState, useRef, useEffect } from 'react';
import type { DragEvent } from 'react';
import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Refresh as RetryIcon,
  PhotoLibrary as PhotoLibraryIcon,
  CameraAlt as CameraIcon,
  ArrowBackIosNew as PrevIcon,
  ArrowForwardIos as NextIcon,
} from '@mui/icons-material';
import { compressImage as coreCompressImage } from '@core/utils/image-compression';
import { ensureHttps } from '@core/utils/formatting';
import { featureFlags } from '@core/config/feature-flags';
import type { ImageResponse } from '@core/types/ticket';

type UploadState = 'pending' | 'compressing' | 'uploading' | 'done' | 'error';

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  state: UploadState;
  error?: string;
}

interface ImageUploaderProps {
  maxImages?: number;
  images: ImageFile[];
  onImagesChange: (images: ImageFile[]) => void;
  onUpload?: (file: File) => Promise<void>;
  disabled?: boolean;
  /** Existing images from API (edit mode) */
  existingImages?: ImageResponse[];
  /** Called when user removes an existing image */
  onExistingImageRemove?: (index: number) => void;
}

function revokePreviewUrl(preview: string) {
  URL.revokeObjectURL?.(preview);
}

export function ImageUploader({
  maxImages = 5,
  images,
  onImagesChange,
  onUpload,
  disabled = false,
  existingImages,
  onExistingImageRemove,
}: ImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const nextImageIdRef = useRef(0);
  const imagesRef = useRef(images);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const existingCount = existingImages?.length ?? 0;
  const totalCount = existingCount + images.length;

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  // Cleanup object URLs on unmount only
  useEffect(() => {
    return () => {
      imagesRef.current.forEach((img) => {
        revokePreviewUrl(img.preview);
      });
    };
  }, []);

  const validateFile = (file: File): string | null => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      return 'Chỉ chấp nhận file JPEG hoặc PNG';
    }
    if (file.size > 5 * 1024 * 1024) {
      return 'Kích thước file tối đa 5MB';
    }
    return null;
  };

  const compressImageLocal = async (file: File): Promise<File> => {
    try {
      // Use the shared WebView-safe compression utility
      // (auto-disables WebWorker in Android WebView)
      return await coreCompressImage(file, {
        maxWidthOrHeight: featureFlags.imageMaxDimension,
        maxSizeMB: featureFlags.imageMaxSizeMB,
      });
    } catch (error) {
      console.error('[ImageUploader] ❌ Compression failed, using original:', error);
      return file; // Fallback to original file
    }
  };

  const handleFileSelect = async (files: FileList | null) => {
      if (!files || disabled) return;

      const newFiles: ImageFile[] = [];
      const fileArray = Array.from(files);

      for (const file of fileArray) {
        if (totalCount + newFiles.length >= maxImages) {
          setError(`Tối đa ${maxImages} ảnh`);
          break;
        }

        const validationError = validateFile(file);
        if (validationError) {
          setError(validationError);
          continue;
        }

        const id = `image-${nextImageIdRef.current++}`;
        const preview = URL.createObjectURL(file);

        newFiles.push({
          id,
          file,
          preview,
          state: 'pending',
        });
      }

      if (newFiles.length > 0) {
        const updatedImages = [...images, ...newFiles];
        imagesRef.current = updatedImages;
        onImagesChange(updatedImages);

        // Start sequential compression and upload (one at a time for mobile perf)
        for (const newFile of newFiles) {
          await processImage(newFile);
        }
      }

      // Reset both inputs
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (cameraInputRef.current) {
        cameraInputRef.current.value = '';
      }
  };

  async function processImage(imageFile: ImageFile) {
    try {
      // Update state to compressing
      updateImageState(imageFile.id, 'compressing');

      const compressedFile = await compressImageLocal(imageFile.file);

      // Update state to uploading
      updateImageState(imageFile.id, 'uploading');

      if (onUpload) {
        await onUpload(compressedFile);
      }

      // Update state to done
      updateImageState(imageFile.id, 'done');
    } catch {
      updateImageState(imageFile.id, 'error', 'Upload failed');
    }
  }

  const updateImageState = (
    id: string,
    state: UploadState,
    error?: string
  ) => {
    const updatedImages = imagesRef.current.map((img) =>
      img.id === id ? { ...img, state, error } : img
    );
    imagesRef.current = updatedImages;
    onImagesChange(updatedImages);
  };

  const handleRemove = (id: string) => {
    if (disabled) return;
    const imageToRemove = images.find((img) => img.id === id);
    if (imageToRemove) {
      revokePreviewUrl(imageToRemove.preview);
    }
    const updatedImages = images.filter((img) => img.id !== id);
    imagesRef.current = updatedImages;
    onImagesChange(updatedImages);
  };

  const handleRetry = (imageFile: ImageFile) => {
    if (disabled) return;
    processImage(imageFile);
  };

  const handleDrag = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const emptySlots = Math.max(0, maxImages - totalCount);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          pb: 1,
        }}
      >
        {/* === Existing images from API (edit mode) === */}
        {existingImages?.map((image, index) => (
          <Box
            key={`existing-${image.reference_id || index}`}
            onClick={() => setLightboxIndex(index)}
            sx={{
              position: 'relative',
              width: 80,
              height: 80,
              flexShrink: 0,
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              cursor: 'pointer',
            }}
          >
            <img
              src={ensureHttps(image.file_url)}
              alt={image.file_name || `Ảnh ${index + 1}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              loading="lazy"
            />

            {/* Delete button for existing image */}
            {!disabled && onExistingImageRemove && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onExistingImageRemove(index); }}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(211, 47, 47, 0.9)',
                  },
                  width: 24,
                  height: 24,
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        ))}

        {/* === New images (file uploads) === */}
        {images.map((image, idx) => (
          <Box
            key={image.id}
            onClick={() => setLightboxIndex(existingCount + idx)}
            sx={{
              position: 'relative',
              width: 80,
              height: 80,
              flexShrink: 0,
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: dragActive ? 'primary.main' : 'divider',
              cursor: 'pointer',
            }}
          >
            <img
              src={image.preview}
              alt="Preview"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />

            {(image.state === 'compressing' || image.state === 'uploading') && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                }}
              >
                <CircularProgress size={24} sx={{ color: 'white' }} />
              </Box>
            )}

            {image.state === 'done' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: '#00A651',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckIcon sx={{ fontSize: 14, color: 'white' }} />
              </Box>
            )}

            {image.state === 'error' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(211, 47, 47, 0.8)',
                }}
              >
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={(e) => { e.stopPropagation(); handleRetry(image); }}
                  sx={{ color: 'white' }}
                >
                  <RetryIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {image.state !== 'compressing' && image.state !== 'uploading' && (
              <IconButton
                size="small"
                disabled={disabled}
                onClick={(e) => { e.stopPropagation(); handleRemove(image.id); }}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  bgcolor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.8)',
                  },
                  width: 24,
                  height: 24,
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        ))}

        {/* === Empty slots === */}
        {Array.from({ length: emptySlots }).map((_, index) => (
          <Box
            key={`empty-${index}`}
            onClick={(e) => {
              if (!disabled) {
                setMenuAnchor(e.currentTarget as HTMLElement);
              }
            }}
            sx={{
              width: 80,
              height: 80,
              flexShrink: 0,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              '&:hover': {
                borderColor: disabled ? 'divider' : 'primary.main',
                bgcolor: disabled ? 'transparent' : 'action.hover',
              },
            }}
          >
            <AddIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Thêm
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Option menu: Chọn ảnh / Chụp ảnh */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem
          onClick={() => {
            console.log('[ImageUploader] 🖼️ Gallery button clicked');
            setMenuAnchor(null);
            try {
              console.log('[ImageUploader] fileInputRef exists:', !!fileInputRef.current);
              fileInputRef.current?.click();
              console.log('[ImageUploader] ✅ fileInput.click() called');
            } catch (err) {
              console.error('[ImageUploader] ❌ fileInput.click() error:', err);
            }
          }}
        >
          <ListItemIcon>
            <PhotoLibraryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Chọn ảnh</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            console.log('[ImageUploader] 📷 Camera button clicked');
            setMenuAnchor(null);
            try {
              console.log('[ImageUploader] cameraInputRef exists:', !!cameraInputRef.current);
              cameraInputRef.current?.click();
              console.log('[ImageUploader] ✅ cameraInput.click() called');
            } catch (err) {
              console.error('[ImageUploader] ❌ cameraInput.click() error:', err);
            }
          }}
        >
          <ListItemIcon>
            <CameraIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Chụp ảnh</ListItemText>
        </MenuItem>
      </Menu>

      {/* Hidden file input for gallery selection */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        multiple
        disabled={disabled}
        onChange={(e) => handleFileSelect(e.target.files)}
        style={{ position: 'fixed', left: '-9999px', top: '-9999px', opacity: 0 }}
      />

      {/* Hidden file input for camera capture */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/jpeg,image/png"
        capture="environment"
        disabled={disabled}
        onChange={(e) => handleFileSelect(e.target.files)}
        style={{ position: 'fixed', left: '-9999px', top: '-9999px', opacity: 0 }}
      />

      {/* Desktop drag & drop hint */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          mt: 1,
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Typography variant="caption" color="text.secondary">
          Kéo thả ảnh vào đây hoặc click vào ô trống để chọn
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* === Lightbox Modal === */}
      <Dialog
        open={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.95)',
            boxShadow: 'none',
            m: 0,
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '100vw',
            height: '100vh',
            borderRadius: 0,
          },
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={() => setLightboxIndex(null)}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.15)',
              zIndex: 10,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Nav: Previous */}
          {lightboxIndex !== null && totalCount > 1 && (
            <IconButton
              onClick={() => setLightboxIndex((lightboxIndex - 1 + totalCount) % totalCount)}
              sx={{
                position: 'absolute',
                left: 8,
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.15)',
                zIndex: 10,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              }}
            >
              <PrevIcon />
            </IconButton>
          )}

          {/* Nav: Next */}
          {lightboxIndex !== null && totalCount > 1 && (
            <IconButton
              onClick={() => setLightboxIndex((lightboxIndex + 1) % totalCount)}
              sx={{
                position: 'absolute',
                right: 8,
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.15)',
                zIndex: 10,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              }}
            >
              <NextIcon />
            </IconButton>
          )}

          {/* Image */}
          {lightboxIndex !== null && (() => {
            const isExisting = lightboxIndex < existingCount;
            const src = isExisting
              ? ensureHttps(existingImages![lightboxIndex].file_url)
              : images[lightboxIndex - existingCount]?.preview;
            const alt = isExisting
              ? (existingImages![lightboxIndex].file_name || `Ảnh ${lightboxIndex + 1}`)
              : `Ảnh ${lightboxIndex + 1}`;
            return src ? (
              <img
                src={src}
                alt={alt}
                style={{
                  maxWidth: '90vw',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                  borderRadius: 4,
                }}
              />
            ) : null;
          })()}

          {/* Counter */}
          {lightboxIndex !== null && totalCount > 1 && (
            <Typography
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'rgba(255,255,255,0.7)',
                fontSize: 14,
              }}
            >
              {lightboxIndex + 1} / {totalCount}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
